import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getAudioContext } from '../util/audio';

export interface LoopRange {
  startTime: number;
  endTime: number;
}

interface AudioControllerState {
  loadAudio: (audioBuffer: AudioBuffer) => void;
  resetAudio: () => void;
  playAudio: (time?: number) => void;
  pauseAudio: () => void;
  stopAudio: () => void;
  backwardAudio: () => void;
  forwardAudio: () => void;
  setIsLoop: (isLoop: boolean) => void;
  trimAudio: () => void;
  cutAudio: () => void;
  audioBuffer?: AudioBuffer;
  isPlaying: boolean;
  isLoop: boolean;
  currentTime: number;
  updateCurrentTime: (time: number) => void;
  loopRange: LoopRange | null;
  setLoopRange: (loopRange: LoopRange | null) => void;
}

const useAudioController = (
  editCallback?: (audioBuffer: AudioBuffer) => void
): AudioControllerState => {
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoop, setIsLoop] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [pausedTime, setPausedTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  // Drag로 선택한 구간
  const [loopRange, setLoopRange] = useState<LoopRange | null>(null);
  // 반복재생 중인 구간
  const [activeLoopRange, setActiveLoopRange] = useState<LoopRange | null>(
    null
  );
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const rafId = useRef<number | null>(null);
  // ISSUE-124. `pauseAudio`의 가드가 읽을 살아있는 isPlaying.
  // 콜백이 클로저 값을 읽으면, 재생 중에 캡처된 pauseAudio가 isPlaying이 이미
  // false가 된 뒤에 호출될 때 가드를 통과해 이미 멈춘 노드에 stop()을 부른다.
  // 커밋 직후 동기적으로 갱신되므로 가드는 항상 최신 값을 본다.
  const isPlayingRef = useRef(false);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const getCurrentTime = useCallback(() => {
    const audioCxt = getAudioContext();
    return pausedTime + audioCxt.currentTime - startTime;
  }, [pausedTime, startTime]);

  const isEnd = useMemo(() => {
    if (!audioBuffer) return false;
    return currentTime === audioBuffer.duration;
  }, [audioBuffer, currentTime]);

  const isLoopStart = useMemo(() => {
    if (!isLoop || !loopRange) return false;
    if (
      (isPlaying && currentTime !== loopRange.startTime) ||
      (!isPlaying && pausedTime !== loopRange.startTime)
    )
      return false;
    return true;
  }, [isLoop, loopRange, isPlaying, currentTime, pausedTime]);

  const isLoopEnd = useMemo(() => {
    if (!isLoop || !loopRange) return false;
    if (
      (isPlaying && currentTime !== loopRange.endTime) ||
      (!isPlaying && pausedTime !== loopRange.endTime)
    )
      return false;
    return true;
  }, [isLoop, loopRange, isPlaying, currentTime, pausedTime]);

  // 프레임 루프 effect와 resetState 양쪽보다 위에 선언되어야 한다: 두 곳 모두
  // 의존성 배열이 렌더 시점에 평가되므로, 아래에 두면 TDZ ReferenceError가 난다.
  // ISSUE-123이 프레임 루프에서 이 함수를 부르게 되면서 제약이 하나 늘었다.
  const resetSourceNode = useCallback(() => {
    if (!sourceNodeRef.current) return;
    sourceNodeRef.current.stop();
    sourceNodeRef.current.disconnect();
    sourceNodeRef.current = null;
  }, [sourceNodeRef]);

  // Update currentTime when audio is playing
  useEffect(() => {
    const updateTime = () => {
      if (!isPlaying || !audioBuffer) return;
      const currentTime = getCurrentTime();
      // currentTime이 duration을 넘어가면 재생을 멈춤
      if (currentTime >= audioBuffer.duration) {
        // ISSUE-123. 여기서 노드를 놓아주지 않으면 재생이 자연 종료된 뒤에도
        // 소진된 BufferSource가 ref에 남고 destination에 연결된 채로 남는다.
        // 소리가 나지는 않으므로(소진된 노드는 무음) 사용자에게 들리는 결함은
        // 아니고, 다음 playAudio나 stopAudio가 우연히 놓아줄 때까지 지속되는
        // 그래프·리소스 누수다.
        resetSourceNode();
        setIsPlaying(false);
        setCurrentTime(audioBuffer.duration);
        return;
      }
      // loop 지점 도달 시, 만약에 activeLoopRange가 loopRange와 다르거나 없는경우 loopRange로 업데이트
      if (
        isLoop &&
        loopRange &&
        currentTime >= loopRange.startTime &&
        currentTime <= loopRange.endTime
      ) {
        setActiveLoopRange(loopRange);
      }

      setCurrentTime(currentTime);
      rafId.current = requestAnimationFrame(updateTime);
    };

    if (isPlaying) {
      rafId.current = requestAnimationFrame(updateTime);
    }
    // ISSUE-110 (7). 취소는 정리 함수 한 곳에서만 하고, 취소한 자리에서 바로
    // null 한다. 이전에는 정리 함수가 null 없이 취소하고, 이어서 실행되는
    // effect 본문의 else 분기가 같은 id를 한 번 더 취소했다 — 소진된 id에
    // 대한 cancelAnimationFrame은 no-op이라 무해했지만, 하나의 id가 두 번
    // 취소되는 동작을 코드가 설명하지 못했다. else 분기를 지울 수 있는 것은
    // null이 이 자리로 함께 옮겨왔기 때문이고, 둘은 분리할 수 없다.
    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [
    isPlaying,
    getCurrentTime,
    audioBuffer,
    loopRange,
    activeLoopRange,
    isLoop,
    resetSourceNode,
  ]);

  // Update currentTime when audio is paused
  useEffect(() => {
    if (isPlaying) return;
    setPausedTime(currentTime);
  }, [isPlaying, currentTime]);

  const resetState = useCallback(() => {
    // 재생 중인 AudioBufferSourceNode는 React state가 아니므로 상태만 지우면
    // UI는 정지했다고 표시하면서 소리는 계속 난다.
    resetSourceNode();
    setIsPlaying(false);
    setIsLoop(false);
    setStartTime(0);
    setPausedTime(0);
    setCurrentTime(0);
    setLoopRange(null);
    setActiveLoopRange(null);
  }, [
    resetSourceNode,
    setIsPlaying,
    setIsLoop,
    setStartTime,
    setPausedTime,
    setCurrentTime,
    setLoopRange,
    setActiveLoopRange,
  ]);

  const loadAudio = useCallback(
    (newAudioBuffer: AudioBuffer) => {
      resetState();
      setAudioBuffer(newAudioBuffer);
    },
    [resetState]
  );

  const resetAudio = useCallback(() => {
    resetState();
    setAudioBuffer(undefined);
  }, [resetState]);

  /* Play audio
    재생 중인 경우 
      repeat이 아닌 경우 -> 처음부터 재생
      repeat인 경우 -> loopRange의 startTime부터 재생
    재생 중이 아닌 경우 -> currentTime부터 재생
  */
  const playAudio = useCallback(
    (time?: number) => {
      if (!audioBuffer) return;
      const audioCxt = getAudioContext();

      resetSourceNode();
      // source노드를 재생성하는 것이 비효율적인 것으로 보이지만 sourcenode는 이 패턴에 최적화되어 있다.
      sourceNodeRef.current = audioCxt.createBufferSource();
      sourceNodeRef.current.buffer = audioBuffer;
      sourceNodeRef.current.connect(audioCxt.destination);

      let startOffset;
      // 시간이 지정된 경우 해당 시간부터 재생
      if (typeof time === 'number') {
        startOffset = time;
      }
      // repeat인 경우 재생중인 여부과 상관없이 startTime부터 재생
      else if (isLoop && loopRange) {
        // 내장 loop를 사용할 경우 현재 시간 계산이 어려움
        startOffset = loopRange.startTime;
      } else {
        startOffset = isPlaying ? 0 : isEnd ? 0 : pausedTime;
      }
      sourceNodeRef.current.start(0, startOffset);

      setStartTime(audioCxt.currentTime);
      setPausedTime(startOffset);
      setIsPlaying(true);
    },
    [
      audioBuffer,
      loopRange,
      isLoop,
      pausedTime,
      isPlaying,
      isEnd,
      resetSourceNode,
    ]
  );

  // ISSUE-110 (6). 아래 리플레이 effect가 `playAudio`를 의존성으로 들고 있으면
  // 순환이 생긴다: playAudio는 `pausedTime`으로 메모이즈되어 있고 본문에서
  // `setPausedTime(startOffset)`을 쓴다. 그래서 재생 머리를 옮기는 리플레이가
  // 새 playAudio 신원을 만들고, effect가 다시 돌고, (currentTime은 아직
  // 갱신 전이라 조건이 그대로여서) 두 번째 BufferSource를 만들었다 버린다.
  // 루프 시작이 현재 pausedTime과 같은 흔한 경우에는 잠복해 있었다.
  //
  // ref로 최신 playAudio를 들고 effect의 의존성에서 뺀다. 여기서 한 렌더
  // 낡은 콜백을 잡아도 안전한 이유가 핵심이다: 이 호출은 **항상 명시적
  // 시간 인자**를 넘기고, playAudio는 `typeof time === 'number'`면
  // startOffset을 그 인자로 정한다. 즉 클로저의 `pausedTime`/`isPlaying`/
  // `isEnd`는 이 경로에서 읽히지 않는다.
  const playAudioRef = useRef(playAudio);
  useEffect(() => {
    playAudioRef.current = playAudio;
  }, [playAudio]);

  // currentTime이 변경될 때 activeLoopRange가 있고, currentTime이 activeLoopRange의 endTime을 넘어가면 startTime으로 이동
  useEffect(() => {
    if (!isPlaying || !activeLoopRange || !isLoop) return;
    const { startTime, endTime } = activeLoopRange;
    if (currentTime >= endTime) {
      playAudioRef.current(startTime);
    }
  }, [isPlaying, currentTime, isLoop, activeLoopRange]);

  // 반복재생 중일 때 loopRange가 변경되면 activeLoopRange를 업데이트
  useEffect(() => {
    if (
      !loopRange ||
      !activeLoopRange ||
      // ISSUE-110 (5). `||` 였다. 두 경계 중 하나만 같아도 조기 반환했으므로,
      // 선택 구간의 한쪽 핸들만 움직이면 반복이 옛 구간에 물린 채로 남았다.
      // 재무장을 건너뛰어야 하는 경우는 양쪽 경계가 **모두** 그대로일 때뿐이다.
      (loopRange.startTime === activeLoopRange.startTime &&
        loopRange.endTime === activeLoopRange.endTime)
    )
      return;
    setActiveLoopRange(loopRange);
  }, [loopRange, activeLoopRange]);

  const pauseAudio = useCallback(() => {
    // 두 번째 pause는 no-op이어야 한다. isPlaying 가드가 없으면 pause가
    // sourceNodeRef를 비우지 않기 때문에 본문이 다시 실행된다.
    // sourceNodeRef는 별개의 조건이다: 같은 배치에서 resetState가 먼저 ref를
    // 비우면 isPlaying은 아직 true인 채로 노드만 사라진다.
    //
    // ISSUE-124: 클로저의 `isPlaying`이 아니라 ref를 읽는다. 두 선택지 중
    // ref를 골랐다 — 다른 선택지(pause에서 sourceNodeRef를 비우기)는 다음
    // playAudio의 resetSourceNode가 마주치는 상태를 바꿔 노드 수명주기까지
    // 건드리는 반면, ref는 가드가 보는 값만 바꾼다. 그 결과 이 콜백은
    // 의존성이 없어져 신원이 고정되고, 소비자 쪽 effect는 덜 재실행된다.
    if (!isPlayingRef.current || !sourceNodeRef.current) return;
    sourceNodeRef.current.stop();
    // ISSUE-125: 일시정지는 재생 머리를 **되감지 않는다**.
    //
    // 아래의 일시정지 effect(`if (isPlaying) return; setPausedTime(currentTime)`)
    // 는 마지막으로 렌더된 프레임 값을 쓴다. 프레임 사이에 멈추면 그 값은
    // 시계보다 뒤에 있으므로, 재생 머리가 최대 한 프레임(브라우저에서 ~16ms)
    // 되감겼다. 여기서 currentTime을 시계 값으로 올려두면 그 effect가 같은
    // 값을 pausedTime에 쓴다. pausedTime에 직접 쓰지 않는 이유는 ISSUE-109가
    // 측정한 대로 그 effect가 어차피 덮어쓰기 때문이다.
    //
    // 함수형 갱신이며 앞으로만 간다. 이것이 없으면 `forwardAudio`가 회귀한다:
    // 같은 배치에서 `setCurrentTime(newEndTime)`을 먼저 부르고 이어서 pause를
    // 부르므로, 무조건 쓰기는 그 의도적 이동을 시계 값으로 덮어쓴다
    // (측정: `pauses when skipping forward reaches the end of the audio`가
    // 10 대신 0을 받고 RED). 되감지 않는다는 규칙 하나로 두 경우가 모두 맞다.
    const clockTime = getCurrentTime();
    setCurrentTime((rendered) => (clockTime > rendered ? clockTime : rendered));
    setIsPlaying(false);
  }, [getCurrentTime]);

  const stopAudio = useCallback(() => {
    resetSourceNode();
    setIsPlaying(false);
    setPausedTime(0);
    requestAnimationFrame(() => {
      setCurrentTime(0);
    });
  }, [resetSourceNode, setIsPlaying, setPausedTime, setCurrentTime]);

  /*
    Backward audio
    loopRange startTime이 있고
    currentTime이 loopRange startTime이 아닌 경우 -> startTime으로 이동
    그렇지 않은 경우 -> 처음으로 이동 (0)
  */
  const backwardAudio = useCallback(() => {
    const newStartTime = isLoopStart ? 0 : loopRange?.startTime || 0;
    setPausedTime(newStartTime);
    setCurrentTime(newStartTime);
    isPlaying && playAudio(newStartTime);
  }, [isLoopStart, loopRange, playAudio, isPlaying]);

  /*
    Forward audio
    loopRange endTime이 있고
    currentTime이 loopRange endTime이 아닌 경우 -> endTime으로 이동
    그렇지 않은 경우 -> 끝으로 이동 (audioBuffer.duration)
  */
  const forwardAudio = useCallback(() => {
    if (!audioBuffer) return;
    const newEndTime = isLoopEnd
      ? audioBuffer.duration
      : loopRange?.endTime || audioBuffer.duration;

    setPausedTime(newEndTime);
    setCurrentTime(newEndTime);
    // 오디오의 끝에 도달한 경우 pause
    if (newEndTime === audioBuffer.duration) {
      pauseAudio();
    } else {
      isPlaying && playAudio(newEndTime);
    }
  }, [audioBuffer, isLoopEnd, loopRange, playAudio, isPlaying, pauseAudio]);

  // 선택한 구간을 제외한 나머지 부분을 자르기 (trim)
  const trimAudio = useCallback(() => {
    if (!audioBuffer || !loopRange) return;

    // Stop audio before edit
    stopAudio();

    const audioCxt = getAudioContext();
    const { startTime, endTime } = loopRange;
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const selectedStartIdx = Math.floor(startTime * sampleRate);
    const selectedEndIdx = Math.floor(endTime * sampleRate);

    const newAudioBuffer = audioCxt?.createBuffer(
      numberOfChannels,
      selectedEndIdx - selectedStartIdx,
      sampleRate
    );

    for (let channel = 0; channel < numberOfChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      const newChannelData = newAudioBuffer?.getChannelData(channel);
      newChannelData?.set(
        channelData.subarray(selectedStartIdx, selectedEndIdx)
      );
    }
    newAudioBuffer && loadAudio(newAudioBuffer);
    editCallback?.(newAudioBuffer);
  }, [audioBuffer, loopRange, stopAudio, loadAudio, editCallback]);

  // 선택 구간만 자르기 (cut)
  const cutAudio = useCallback(() => {
    if (!audioBuffer || !loopRange) return;

    // Stop audio before edit
    stopAudio();

    const audioCxt = getAudioContext();
    const { startTime, endTime } = loopRange;
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const selectedStartIdx = Math.floor(startTime * sampleRate);
    const selectedEndIdx = Math.floor(endTime * sampleRate);
    const endIdx = Math.floor(audioBuffer.duration * sampleRate);

    const newAudioBuffer = audioCxt?.createBuffer(
      numberOfChannels,
      endIdx - selectedEndIdx + selectedStartIdx,
      sampleRate
    );

    for (let channel = 0; channel < numberOfChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      const newChannelData = newAudioBuffer?.getChannelData(channel);

      newChannelData?.set(channelData.subarray(0, selectedStartIdx), 0);
      newChannelData?.set(
        channelData.subarray(selectedEndIdx, endIdx),
        selectedStartIdx
      );
    }
    newAudioBuffer && loadAudio(newAudioBuffer);
    editCallback?.(newAudioBuffer);
  }, [audioBuffer, loopRange, stopAudio, loadAudio, editCallback]);

  const updateCurrentTime = useCallback(
    (time: number) => {
      // 현재 반복재생 구간이 지정되어 있는데, 해당 범위를 벗어나면(예를 들어 시작점, 끝점 벗어나도록 재생시간을 변경)
      // 현재 활성화된 반복재생구간을 해제
      if (activeLoopRange) {
        const { startTime, endTime } = activeLoopRange;
        if (time < startTime || time > endTime) {
          activeLoopRange && setActiveLoopRange(null);
        }
      }
      // 재생중인 경우 해당 시간부터 재생
      if (isPlaying) {
        playAudio(time);
      }
      // 재생중이 아닌 경우 currentTime만 업데이트
      else {
        setCurrentTime(time);
      }
    },
    [activeLoopRange, isPlaying, playAudio]
  );

  // 페이지 전환이 일어나면 audio를 정지
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  return {
    loadAudio,
    resetAudio,
    playAudio,
    pauseAudio,
    stopAudio,
    backwardAudio,
    forwardAudio,
    setIsLoop,
    trimAudio,
    cutAudio,
    audioBuffer,
    isPlaying,
    isLoop,
    currentTime,
    updateCurrentTime,
    loopRange,
    setLoopRange,
  };
};

export default useAudioController;
